import { Link } from "react-router-dom";
import { StatusBadge, Timestamp } from "@/design-system/components/ui";

/**
 * Shared presentational pieces for the client-fetching "panel" widgets —
 * the same row / figure / bar visual language as the composite widgets
 * (CompositeWidgets.tsx), but fed by data the panel fetched itself.
 */

export interface PanelRowItem {
  id: string;
  primary: string;
  secondary?: string | null;
  badge?: string | null;
  badgeTone?: "neutral" | "progress" | "info" | "warn" | "ok" | "danger";
  /** Right-aligned plain text (e.g. an amount) rendered before the timestamp. */
  meta?: string | null;
  timestamp?: string | null;
  href?: string | null;
}

export function PanelRows({ items }: { items: PanelRowItem[] }) {
  return (
    <ul className="m-0 p-0 list-none divide-y divide-border-light">
      {items.map((row) => {
        const body = (
          <div className="flex items-center justify-between gap-space-2 py-space-2">
            <div className="min-w-0">
              <div className="text-small font-semibold text-onyx truncate">{row.primary}</div>
              {row.secondary && (
                <div className="text-label text-gray-mid truncate">{row.secondary}</div>
              )}
            </div>
            <div className="flex items-center gap-space-2 shrink-0">
              {row.meta && (
                <span className="text-label text-gray-mid font-mono tabular-nums">{row.meta}</span>
              )}
              {row.badge && <StatusBadge tone={row.badgeTone ?? "neutral"}>{row.badge}</StatusBadge>}
              {row.timestamp && (
                <span className="text-label text-gray-mid">
                  <Timestamp value={row.timestamp} />
                </span>
              )}
            </div>
          </div>
        );
        return (
          <li key={row.id}>
            {row.href ? (
              <Link
                to={row.href}
                className="block hover:bg-white-warm -mx-space-2 px-space-2 rounded-sm"
              >
                {body}
              </Link>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface PanelFigure {
  label: string;
  value: string | number;
  /** Tinted via the semantic status tokens (numeric zeros stay neutral). */
  tone?: "warning" | "error" | "info" | "ok";
}

export function PanelFigures({ figures }: { figures: PanelFigure[] }) {
  return (
    <div className="flex flex-wrap gap-x-space-5 gap-y-space-3">
      {figures.map((f) => (
        <div key={f.label}>
          <div
            className={`font-display font-bold text-h2 leading-tight tabular-nums ${figureToneClass(f)}`}
          >
            {typeof f.value === "number" ? f.value.toLocaleString("en-US") : f.value}
          </div>
          <div className="font-display text-label uppercase tracking-label text-gray-mid">
            {f.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function figureToneClass(f: PanelFigure): string {
  if (!f.tone || f.value === 0) return "text-onyx";
  switch (f.tone) {
    case "warning":
      return "text-status-warning";
    case "error":
      return "text-status-error";
    case "info":
      return "text-status-info";
    case "ok":
      return "text-status-success";
  }
}

export interface PanelBarRow {
  key: string;
  label: string;
  count: number;
}

export function PanelBars({
  rows,
  barClassName = "bg-gold",
}: {
  rows: PanelBarRow[];
  barClassName?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
      {rows.map((r) => (
        <li key={r.key} className="grid grid-cols-[1fr_auto] gap-space-3 items-center">
          <div className="min-w-0">
            <div className="text-small text-onyx truncate">{r.label}</div>
            <div className="h-2 bg-white-warm rounded-pill overflow-hidden">
              <div
                className={`h-full ${barClassName}`}
                style={{ width: `${(r.count / max) * 100}%` }}
                aria-hidden
              />
            </div>
          </div>
          <span className="text-small font-semibold text-onyx tabular-nums">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

/** Muted single-line "all clear" note for panels that keep their figures
 *  visible even when the queue below them is empty. */
export function PanelClearLine({ text }: { text: string }) {
  return <div className="text-small text-gray-mid mt-space-3">{text}</div>;
}
